import { Section } from '@components/section/Section'
import { SectionTitle } from '@components/section/SectionTitle'
import { ReferralCodeCard, ReferredUsersList } from '@features/referral/components'
import {
  usePartnerReferralCodes,
  usePartnerReferredUsers
} from '@services/referral'
import { Loader2, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Card } from '@/components/ui/card'

/**
 * Partner referral codes + referred users.
 * `bare` renders plain cards (for the dedicated page); the default renders the
 * collapsible Section used inside admin Settings.
 */
export function Referrals({ bare = false }: { bare?: boolean }) {
  const { t } = useTranslation('settings')
  const {
    data: referralCodes,
    isLoading: codesLoading,
    error: codesError
  } = usePartnerReferralCodes()
  const {
    data: referredUsers,
    isLoading: usersLoading,
    error: usersError
  } = usePartnerReferredUsers()

  const isExpired = referralCodes?.expires_at
    ? new Date(referralCodes.expires_at) < new Date()
    : false

  if (codesError || usersError) {
    const error = (
      <div className="rounded-2xl border border-destructive/20 bg-destructive/[0.04] p-4">
        <p className="m-0 font-semibold text-destructive">
          {t('referrals.error')}
        </p>
        <p className="m-0 mt-1 text-sm text-muted-foreground">
          {t('referrals.errorDescription')}
        </p>
      </div>
    )
    return bare ? (
      <Card className="p-5 sm:p-6">{error}</Card>
    ) : (
      <Section
        title={
          <SectionTitle
            title={t('referrals.title')}
            icon={<Users className="h-4 w-4" />}
          />
        }
      >
        <div className="m-5">{error}</div>
      </Section>
    )
  }

  const codesBlock = (
    <div>
      <h3 className="m-0 font-display text-base font-bold text-ink">
        {t('referrals.myCodes')}
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {t('referrals.codesDescription')}
      </p>

      {codesLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ReferralCodeCard
            title={t('referrals.instagramCode')}
            code={referralCodes?.instagram_code || null}
            type="instagram"
            usageCount={
              typeof referralCodes?.instagram_usage_count === 'number'
                ? referralCodes.instagram_usage_count
                : 0
            }
            isExpired={isExpired}
          />
          <ReferralCodeCard
            title={t('referrals.facebookLink')}
            code={referralCodes?.facebook_link_code || null}
            type="facebook"
            clickCount={
              typeof referralCodes?.facebook_click_count === 'number'
                ? referralCodes.facebook_click_count
                : 0
            }
            isExpired={isExpired}
          />
        </div>
      )}
    </div>
  )

  const usersBlock = (
    <div>
      <h3 className="m-0 font-display text-base font-bold text-ink">
        {t('referrals.referredUsers.title')}
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {t('referrals.referredUsers.description')}
      </p>

      {referredUsers && (
        <div className="mt-4">
          <ReferredUsersList
            users={referredUsers.users}
            stats={referredUsers.stats}
            loading={usersLoading}
          />
        </div>
      )}
    </div>
  )

  if (bare) {
    return (
      <div className="space-y-6">
        <Card className="p-5 sm:p-6">{codesBlock}</Card>
        <Card className="p-5 sm:p-6">{usersBlock}</Card>
      </div>
    )
  }

  return (
    <Section
      title={
        <SectionTitle
          title={t('referrals.title')}
          icon={<Users className="h-4 w-4" />}
        />
      }
    >
      <div className="space-y-8 p-5 pt-2">
        {codesBlock}
        {usersBlock}
      </div>
    </Section>
  )
}
